import { CopyIcon, PencilIcon, PlusIcon, StarIcon } from 'lucide-react';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { DashboardPreset } from '@/entities/dashboards';
import { useDashboardPresets } from '@/entities/settings';
import { SharePresetDialog } from '@/entities/shared-presets';
import { cn } from '@/shared/lib';
import { ConfirmationButton, IconButton } from '@/shared/ui';

type Props = {
  /**
   * Применённый дашборд сменили (сохранили копию) или сняли (удалили): страница показывает
   * новый либо возвращается к стандартному, а если она была открыта разделом прежнего
   * дашборда — уходит и с его адреса.
   */
  onActivePresetChange: (presetId: string | null) => void;
  /** Завести новый дашборд: страница включает режим редактирования на пустой сетке */
  onCreatePreset: () => void;
  /** Править состав применённого дашборда: страница включает режим редактирования на его сетке */
  onEditPreset: (preset: DashboardPreset) => void;
};

/**
 * Действия над применённым дашбордом: завести новый, редактировать, поделиться, закрепить в меню,
 * удалить. Без применённого дашборда остаётся только заведение нового — править и закреплять нечего.
 *
 * Получатель совместного дашборда его не правит: вместо правки он сохраняет личную копию, а удаление
 * убирает дашборд только из его списка. Закрепление у каждого своё.
 */
export const DashboardPresetActions: FC<Props> = ({ onActivePresetChange, onCreatePreset, onEditPreset }) => {
  const { t } = useTranslation('qualityDashboard');
  const { activePreset, isSharing, remove, setPinned, share, saveCopy } = useDashboardPresets();

  // Страница уходит к стандартному дашборду только с удалением: отказ сервера в удалении совместного
  // оставляет его применённым, и уйти со страницы значило бы снять его
  const handleDelete = useCallback(() => {
    if (!activePreset) return;
    remove(activePreset.id, () => onActivePresetChange(null));
  }, [activePreset, remove, onActivePresetChange]);

  const handleSaveCopy = useCallback(() => {
    if (!activePreset) return;
    const copyId = saveCopy(activePreset.id, t('presets.copyName', { name: activePreset.name }));
    if (copyId) onActivePresetChange(copyId);
  }, [activePreset, saveCopy, onActivePresetChange, t]);

  const pinLabel = activePreset?.pinned ? t('presets.unpinFromMenu') : t('presets.pinToMenu');
  const isRemovedForSelfOnly = Boolean(activePreset?.isShared && !activePreset.isEditable);

  return (
    <div className="flex items-center gap-1">
      <IconButton
        variant="ghost"
        size="icon"
        label={t('presets.create')}
        icon={<PlusIcon className="size-4" />}
        onClick={onCreatePreset}
      />

      {activePreset && (
        <>
          {activePreset.isEditable ? (
            <IconButton
              variant="ghost"
              size="icon"
              label={t('presets.edit')}
              icon={<PencilIcon className="size-4" />}
              onClick={() => onEditPreset(activePreset)}
            />
          ) : (
            <IconButton
              variant="ghost"
              size="icon"
              label={t('presets.saveCopy')}
              icon={<CopyIcon className="size-4" />}
              onClick={handleSaveCopy}
            />
          )}
          {activePreset.isEditable && (
            <SharePresetDialog
              title={t('presets.shareTitle', { name: activePreset.name })}
              triggerLabel={t('presets.share')}
              recipients={activePreset.isShared ? activePreset.recipients : []}
              onShare={(recipientIds, onShared) => share(activePreset.id, recipientIds, onShared)}
              isSharing={isSharing}
            />
          )}
          <IconButton
            variant="ghost"
            size="icon"
            label={pinLabel}
            aria-pressed={activePreset.pinned}
            icon={<StarIcon className={cn('size-4', activePreset.pinned && 'fill-current')} />}
            onClick={() => setPinned(activePreset.id, !activePreset.pinned)}
          />
          <ConfirmationButton
            label={isRemovedForSelfOnly ? t('presets.removeShared') : t('presets.deletePreset')}
            title={
              isRemovedForSelfOnly
                ? t('presets.removeSharedConfirmTitle', { name: activePreset.name })
                : t('presets.deleteConfirmTitle', { name: activePreset.name })
            }
            description={
              isRemovedForSelfOnly ? t('presets.removeSharedConfirmDescription') : t('presets.deleteConfirmDescription')
            }
            onConfirm={handleDelete}
            trigger={{ size: 'icon' }}
          />
        </>
      )}
    </div>
  );
};
